import { z } from 'zod';
import { FleetPickQueueSchema, FleetRunningStateSchema } from './fleet-next-job.schemas';
import { FleetLaneNameSchema, FleetProjectNameSchema } from './fleet-status.schemas';

/**
 * `POST /v1/fleet/lanes` — a lane worker says what lane it serves when it starts (OPS-69). The backend keeps the
 * latest definition per project and lane for the operator's board, and nothing else reads it: the flow stays in the
 * project's config and `jobs/next` still takes what each request carries.
 *
 * Mirrors fleet-agent's `src/presenter/contract/fleet-lanes.schemas.ts`; keep the shapes identical.
 */

const LabelSchema = z.string().min(1).max(128);

export const ReportFleetLaneBodySchema = z.object({
  project: FleetProjectNameSchema,
  lane: FleetLaneNameSchema,
  worker: z.string().min(1).max(128),
  /** The lane's place in the project's declaration order, from 0 — the board's column order. */
  position: z.number().int().min(0).max(63),
  /** The lane's state queues, exactly as its `jobs/next` requests send them. */
  pickFrom: z.array(FleetPickQueueSchema).max(10),
  running: FleetRunningStateSchema,
  /** How many workers may run the lane at once; `null` unlimited. */
  maxWorkers: z.number().int().positive().nullable(),
  /** The lane works PRs a reviewer asked to fix before any ticket (a `github` pick-from first). */
  prFirst: z.boolean(),
  /**
   * The project's hold labels: a ticket parked for a person, one a person builds by hand, and one waiting on an
   * unmerged blocker (fleet-agent ADR 0020). `waiting` is absent from hosts that predate it.
   */
  holds: z.object({
    parked: LabelSchema.nullable(),
    assisted: LabelSchema.nullable(),
    waiting: LabelSchema.nullable().optional(),
  }),
});
export type ReportFleetLaneBody = z.infer<typeof ReportFleetLaneBodySchema>;

export const ReportFleetLaneResponseSchema = z.object({ ok: z.literal(true) });
export type ReportFleetLaneResponse = z.infer<typeof ReportFleetLaneResponseSchema>;

const TicketSchema = z.string().min(1).max(64);

/** `blocker` must be built before `blocked`: a tracker relation, or a slice doc's `blockedBy` (fleet-agent ADR 0020). */
export const FleetLaneBlockerPairSchema = z.object({ blocker: TicketSchema, blocked: TicketSchema });
export type FleetLaneBlockerPair = z.infer<typeof FleetLaneBlockerPairSchema>;

/**
 * `POST /v1/fleet/lanes/blockers-first` — a worker's blocker watch sends a lane's blocker pairs, and the backend moves
 * every blocker the lane's queue shows below what it blocks to directly above it (OPS-86), the operator's drag.
 */
export const OrderFleetLaneBlockersBodySchema = z.object({
  project: FleetProjectNameSchema,
  lane: FleetLaneNameSchema,
  pairs: z.array(FleetLaneBlockerPairSchema).max(200),
});
export type OrderFleetLaneBlockersBody = z.infer<typeof OrderFleetLaneBlockersBodySchema>;

/** A move made: `ticket` now sits directly above `before`. */
export const FleetLaneBlockerMoveSchema = z.object({ ticket: TicketSchema, before: TicketSchema });
export type FleetLaneBlockerMove = z.infer<typeof FleetLaneBlockerMoveSchema>;

export const OrderFleetLaneBlockersResponseSchema = z.object({ moved: z.array(FleetLaneBlockerMoveSchema) });
export type OrderFleetLaneBlockersResponse = z.infer<typeof OrderFleetLaneBlockersResponseSchema>;
